import React from 'react';
import { useSpring, useScroll } from '@react-spring/web';
import Image from 'next/image';
import { v4 as uuidv4 } from 'uuid';

//feature layer
import LayerOne from 'Public/graphics/fiftyTwoThings/52_Weeks_Layer1-min.png';

//bg layers
import LayerTwo from 'Public/graphics/fiftyTwoThings/52_Weeks_Layer2-min.png';
import LayerThree from 'Public/graphics/fiftyTwoThings/52_Weeks_Layer3-min.png';
import LayerFour from 'Public/graphics/fiftyTwoThings/52_Weeks_Layer4-min.png';

import IconButtonLink, { LinkIconButtonProps } from 'Elements/Button/Icon';

import {
    Wrapper,
    Container,
    Heading,
    Layer,
    FeatureLayer,
    Subheading,
    Text,
    LinkWrapper,
    FeatureLayerWrapper
} from './FiftyTwoWeeks.styled';

export interface Props {
    id: string;
    heading: {
        number: string;
        subheading: string;
    };
    text: string;
    link?: LinkIconButtonProps;
}

const FiftyTwoWeeks: React.FC<Props> = ({ id, heading, link, text }) => {
    const { scrollY } = useScroll();

    const [featureSpring] = useSpring(() => ({
        from: {
            opacity: 0,
            transform: 'translateY(-300px) rotateX(270deg)'
        },
        to: {
            opacity: 1,
            transform: 'translateY(0px) rotateX(360deg)'
        },
        config: { duration: 500 }
    }));

    const [layerSpring] = useSpring(() => ({
        from: {
            opacity: 0,
            y: -100
        },
        to: {
            opacity: 1,
            y: 0
        }
    }));

    const textSpring = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        delay: 200,
        config: { duration: 500 }
    });

    const layerValuesArray = [
        { multiplier: 0.8, image: LayerTwo, zIndex: 1 },
        { multiplier: 0.6, image: LayerThree, zIndex: 3 },
        { multiplier: 0.4, image: LayerFour, zIndex: 2 }
    ];

    return (
        <Wrapper id={id}>
            <Container>
                <FeatureLayerWrapper>
                    <FeatureLayer style={featureSpring} $zIndex={4}>
                        <Image
                            src={LayerOne}
                            alt=""
                            fill
                            style={{ objectFit: 'contain' }}
                            priority
                        />
                        <Heading
                            style={textSpring}
                            aria-label={`${heading.number} ${heading.subheading}`}
                        >
                            {heading.number}
                            <Subheading>{heading.subheading}</Subheading>
                        </Heading>
                    </FeatureLayer>
                    <Text style={textSpring}>{text}</Text>
                </FeatureLayerWrapper>
                {layerValuesArray.map((layer, index) => (
                    <Layer
                        style={{
                            ...layerSpring,
                            transform: scrollY.to(
                                y => `translateY(${Math.round(y * layer.multiplier)}px)`
                            ),
                            transition: `transform ${index * 0.15}s ease-out`
                        }}
                        $zIndex={layer.zIndex}
                        key={`layer-${uuidv4()}`}
                    >
                        <Image
                            src={layer.image}
                            alt=""
                            fill
                            style={{ objectFit: 'contain' }}
                            priority
                        />
                    </Layer>
                ))}

                {link && (
                    <LinkWrapper>
                        <IconButtonLink {...link} />
                    </LinkWrapper>
                )}
            </Container>
        </Wrapper>
    );
};

export default FiftyTwoWeeks;
